// F7 / brief §D: Admin → Webhooks. Outbound, HMAC-signed org webhooks. The signing secret is
// returned exactly once, by the create call, and shown here in a copyable callout that is gone
// as soon as it is dismissed — no endpoint can return it again (docs/webhooks.md).
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { Badge, Button, Callout, Card, Checkbox, Input, LinkField } from "@grackle/ui";
import { SettingsSection } from "./SettingsSection";
import { ApiFail } from "../api/client";
import { formatRelativeTime } from "../library/format";
import {
  WEBHOOK_EVENTS,
  createWebhook,
  deleteWebhook,
  listWebhookDeliveries,
  listWebhooks,
  redeliverWebhook,
  updateWebhook,
  type WebhookCreated,
  type WebhookDelivery,
  type WebhookOut,
} from "../notifications/api";
import ConfirmDialog from "../components/ConfirmDialog";
import type { AdminOutletContext } from "./AdminLayout";

function DeliveryLog({ orgId, webhook }: { orgId: string; webhook: WebhookOut }) {
  const [rows, setRows] = useState<WebhookDelivery[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listWebhookDeliveries(orgId, webhook.id)
      .then((res) => setRows(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load deliveries."));
  }, [orgId, webhook.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function redeliver(id: string) {
    setError(null);
    try {
      await redeliverWebhook(orgId, webhook.id, id);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't redeliver that.");
    }
  }

  if (!rows) return error ? <Callout tone="danger" icon="warning-circle">{error}</Callout> : <div className="admin-row-sub">Loading…</div>;

  return (
    <div>
      {error ? <Callout tone="danger" icon="warning-circle">{error}</Callout> : null}
      {rows.length === 0 ? <div className="admin-row-sub">No deliveries yet.</div> : null}
      {rows.length > 0 ? (
        <Card size="flush" className="admin-table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="gk-overline">Event</th>
              <th className="gk-overline">Status</th>
              <th className="gk-overline">Code</th>
              <th className="gk-overline">Duration</th>
              <th className="gk-overline">When</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td className="mono">{d.event_type}</td>
                <td>
                  <Badge tone={d.status === "sent" ? "success" : d.status === "pending" ? "neutral" : "record"}>
                    {d.status}
                  </Badge>
                </td>
                <td>{d.response_code ?? "—"}</td>
                <td>{d.duration_ms != null ? `${d.duration_ms} ms` : "—"}</td>
                <td>{formatRelativeTime(d.created_at)}</td>
                <td>
                  <Button size="sm" variant="secondary" icon="arrow-counter-clockwise" onClick={() => void redeliver(d.id)}>
                    Redeliver
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </Card>
      ) : null}
    </div>
  );
}

function EventPicker({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  return (
    <fieldset className="webhook-events">
      <legend className="admin-row-sub">Events</legend>
      {WEBHOOK_EVENTS.map((ev) => (
        <label key={ev} className="webhook-events__row">
          <Checkbox
            size="sm"
            checked={value.includes(ev)}
            label={ev}
            onChange={(on) => onChange(on ? [...value, ev] : value.filter((e) => e !== ev))}
          />
          <span className="mono">{ev}</span>
        </label>
      ))}
    </fieldset>
  );
}

export default function WebhooksPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [hooks, setHooks] = useState<WebhookOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<WebhookCreated | null>(null);
  const [logFor, setLogFor] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<WebhookOut | null>(null);

  const load = useCallback(() => {
    listWebhooks(org.id)
      .then((res) => setHooks(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load webhooks."));
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      setCreated(await createWebhook(org.id, { url: url.trim(), events }));
      setUrl("");
      setEvents([]);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't create that webhook.");
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(h: WebhookOut) {
    setError(null);
    try {
      await updateWebhook(org.id, h.id, { active: !h.active });
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't update that webhook.");
    }
  }

  async function onDelete(h: WebhookOut) {
    setError(null);
    try {
      await deleteWebhook(org.id, h.id);
      setToDelete(null);
      if (logFor === h.id) setLogFor(null);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't delete that webhook.");
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Webhooks</h1>
          <p>
        Grackle POSTs a signed JSON event to your HTTPS endpoint (IDs and links only — never transcript text). Verify
        the <span className="mono">Grackle-Signature</span> header with the secret shown once when you create the
        endpoint; see docs/webhooks.md for Node, Python and Rust snippets.
          </p>
        </div>
      </header>

      {created ? (
        <Callout tone="success" icon="key">
          <div>
            <strong>Copy your signing secret now.</strong> It won't be shown again.
          </div>
          <LinkField url={created.secret} href={created.secret} />
          <Button size="sm" variant="secondary" onClick={() => setCreated(null)}>
            I've saved it
          </Button>
        </Callout>
      ) : null}
      {error ? <Callout tone="danger" icon="warning-circle">{error}</Callout> : null}

      <SettingsSection title="Add endpoint" description="An HTTPS URL and the events it should receive.">
        <form className="admin-stack" onSubmit={(e) => void onCreate(e)}>
          <Input
            label="Endpoint URL"
            placeholder="https://example.com/grackle-webhook"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <EventPicker value={events} onChange={setEvents} />
          <div>
            <Button type="submit" disabled={creating || !url.trim() || events.length === 0}>
              {creating ? "Creating…" : "Create endpoint"}
            </Button>
          </div>
        </form>
      </SettingsSection>

      <div className="admin-stack">
        {hooks === null ? <div className="admin-row-sub">Loading…</div> : null}
        {hooks?.length === 0 ? <div className="admin-row-sub">No webhook endpoints yet.</div> : null}
        {hooks?.map((h) => (
          <Card key={h.id}>
            <div className="list-row admin-webhook-row">
              <div className="list-row__body">
                <div className="list-row__title mono">{h.url_masked}</div>
                <div className="admin-row-sub">{h.events.join(", ")}</div>
                {h.disabled_at ? (
                  <Callout tone="now" icon="warning-circle">
                    Disabled automatically after {h.consecutive_failures} consecutive failures. Re-activate once the
                    endpoint is fixed.
                  </Callout>
                ) : null}
              </div>
              <Badge tone={h.active ? "success" : "neutral"}>{h.active ? "Active" : "Inactive"}</Badge>
              <Button size="sm" variant="secondary" onClick={() => void toggleActive(h)}>
                {h.active ? "Deactivate" : "Activate"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setLogFor(logFor === h.id ? null : h.id)}>
                {logFor === h.id ? "Hide deliveries" : "Deliveries"}
              </Button>
              <Button size="sm" variant="danger" icon="trash" onClick={() => setToDelete(h)}>
                Delete
              </Button>
            </div>
            {logFor === h.id ? <DeliveryLog orgId={org.id} webhook={h} /> : null}
          </Card>
        ))}
      </div>

      {toDelete ? (
        <ConfirmDialog
          open
          title="Delete this webhook?"
          message={`Grackle stops sending events to ${toDelete.url_masked}. Its delivery log is deleted with it.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => void onDelete(toDelete)}
          onClose={() => setToDelete(null)}
        />
      ) : null}
    </>
  );
}
